import { useMemo, useState } from "react";
import { format, subDays } from "date-fns";
import { toast } from "sonner";
import { CheckCheck, Inbox, LayoutGrid } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import SectionCard from "@/components/common/SectionCard";
import EmptyState from "@/components/common/EmptyState";
import SearchField from "@/components/common/SearchField";
import NotificationFilterTabs from "@/components/account/NotificationFilterTabs";
import NotificationRow from "@/components/account/NotificationRow";
import NotificationsSkeleton from "@/components/account/skeletons/NotificationsSkeleton";
import { Button } from "@/components/ui/button";
import ExportButton from "@/components/common/ExportButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { downloadCsv } from "@/utils/common.utils";
import { NOTIFICATION_FILTERS, NOTIFICATION_RANGE_OPTIONS } from "@/constant/account.constant";
import { notificationDayBucket } from "@/utils/account.utils";
import { useGetNotifications, useMarkNotificationsRead } from "@/hooks/useNotifications";
import { getNotificationsService } from "@/services/notifications.service";
import type { AppNotification, NotificationCategory } from "@/types/notifications.types";

const PAGE_SIZE = 30;

export default function NotificationsPage() {
  const [category, setCategory] = useState<NotificationCategory | "">("");
  const [range, setRange] = useState("30");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Search, range, and tab are all server-side query params now — the page used
  // to filter a nine-row fixture in memory.
  const params = useMemo(() => ({
    ...(range === "all" ? {} : { from: format(subDays(new Date(), Number(range) - 1), "yyyy-MM-dd") }),
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(category ? { category } : {}),
    page,
    limit: PAGE_SIZE,
  }), [range, search, category, page]);

  const { data, isLoading } = useGetNotifications(params);
  const { mutate: markRead } = useMarkNotificationsRead();

  const counts = data?.counts;
  const pagination = data?.pagination;

  // Rows arrive newest-first from the server, so a single pass preserves order
  // within each day bucket. Depends on `data` rather than a `?? []` fallback,
  // which would be a fresh array identity on every render.
  const groups = useMemo(() => {
    const map = new Map<string, AppNotification[]>();
    for (const item of data?.notifications ?? []) {
      const key = notificationDayBucket(item.at);
      map.set(key, [...(map.get(key) ?? []), item]);
    }
    return Array.from(map.entries());
  }, [data]);

  const onFilterChange = (value: string) => {
    setCategory(value as NotificationCategory | "");
    setPage(1);
  };

  const exportCsv = async () => {
    // Export the whole filtered set, not the page on screen.
    const all = await getNotificationsService({ ...params, page: 1, limit: 200 });
    if (!all.notifications.length) {
      toast.info("There are no notifications to export");
      return;
    }
    downloadCsv({
      filename: "notifications.csv",
      rows: [
        ["Date", "Category", "Type", "Title", "Description", "Actor", "Record", "Read"],
        ...all.notifications.map((n) => [
          n.at, n.category, n.type, n.title, n.description, n.actor_name, n.record_label, n.read ? "yes" : "no",
        ]),
      ],
    });
  };

  return (
    <div className="font-dashboard" data-testid="notifications-page">
      <PageHeader
        title="Notifications"
        description="Everything that moved on your deals, newest first."
        className="mb-7"
        actions={
          <>
            <Select value={range} onValueChange={(value) => { setRange(value); setPage(1); }}>
              <SelectTrigger className="h-11 w-auto gap-2 rounded-xl px-3" data-testid="notifications-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end">
                {NOTIFICATION_RANGE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="dashboardOutline"
              size="lg"
              className="h-11 !rounded-xl px-3"
              disabled={!data?.unread_count}
              onClick={() => markRead({ all: true })}
              data-testid="notifications-mark-all-read"
            >
              <CheckCheck data-icon="inline-start" />
              Mark all read{data?.unread_count ? ` (${data.unread_count})` : ""}
            </Button>
            <ExportButton
              onExport={exportCsv}
              label="Export CSV"
              className="h-11 !rounded-xl px-3"
              testId="notifications-export-csv"
            />
          </>
        }
      />

      <SearchField
        value={search}
        onChange={(event) => { setSearch(event.target.value); setPage(1); }}
        onClear={() => { setSearch(""); setPage(1); }}
        placeholder="Search notifications…"
        className="mb-4 w-full sm:w-80"
        testId="notifications-search"
      />

      <NotificationFilterTabs
        items={NOTIFICATION_FILTERS.map((f) => ({
          value: f.key,
          label: f.label,
          // Counts ignore the tab selection so each tab can show what selecting
          // it would reveal. Default to 0 while the first fetch is in flight.
          count: (f.key === "" ? counts?.all : counts?.[f.key as NotificationCategory]) ?? 0,
          icon: f.key === "" ? LayoutGrid : undefined,
        }))}
        value={category}
        onValueChange={onFilterChange}
        className="mb-6"
      />

      {isLoading ? (
        <NotificationsSkeleton />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No notifications found"
          description={search || category ? "Try a different search or category." : "Activity on your deals will show up here."}
        />
      ) : (
        <>
          {groups.map(([label, rows]) => (
            <div key={label} className="mb-6">
              <div className="mb-3 flex items-center gap-3">
                <h2 className="text-sm font-semibold text-dash-ink">{label}</h2>
                <div className="h-px flex-1 bg-dash-border" />
              </div>
              <SectionCard contentClassName="p-0">
                {rows.map((n) => (
                  <NotificationRow
                    key={n.id}
                    notification={n}
                    onOpen={(item) => { if (!item.read) markRead({ ids: [item.id] }); }}
                  />
                ))}
              </SectionCard>
            </div>
          ))}

          {!!pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between gap-3" data-testid="notifications-pagination">
              <p className="text-xs text-dash-faint">
                Page {pagination.page} of {pagination.totalPages} · {pagination.total} notifications
              </p>
              <div className="flex gap-2">
                <Button type="button" variant="dashboardOutline" size="sm" disabled={pagination.page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </Button>
                <Button type="button" variant="dashboardOutline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
